import clsx from "clsx";
import { DetailTable } from "./DetailTable";
import { useFinancialHighlightsData } from "../hooks/useFinancialHighlightsData";
import { JumpToSection } from "./JumpToSection";
import { MemoTable } from "./MemoTable";
import { useFinanceLab } from "@/entities/FinanceLab";
import { useApiQuery } from "@/api";
import { getCompany } from "@/entities/Company";
import { Spinner } from "@/ui/atoms/Spinner";
import { ArrowPathIcon } from "@heroicons/react/24/outline";

export function FinancialHighlights() {
    const {
        generalData,
        balanceSheetData,
        incomeStatementData,
        capitalStructureData,
        metricsProjectionsData,
        projectedFinancialsData,
        isLoading,
    } = useFinancialHighlightsData();

    const company = useApiQuery(getCompany);
    const { startGenerateMetrics, isPolling, displayMessage } = useFinanceLab();

    const isLoadingMetrics = isLoading || isPolling;

    const projectedFinancialsColumns = [
        { field: "metric", headerName: "", flex: 1.5, minWidth: 200 },
        { field: "2026", headerName: "2026", flex: 1, minWidth: 120 },
        { field: "2027", headerName: "2027", flex: 1, minWidth: 120 },
        { field: "2028", headerName: "2028", flex: 1, minWidth: 120 },
    ];

    const handleGenerateMetrics = () => {
        if (company.data?.uuid) {
            startGenerateMetrics({ companyId: company.data.uuid });
        }
    };

    const getFormattedMessage = () => {
        if (!isPolling) return "";

        const formattedText =
            displayMessage && company.data?.uuid && company.data?.name
                ? displayMessage.replace(company.data.uuid, company.data.name)
                : displayMessage;

        return formattedText || "This may take a minute...";
    };

    return (
        <>
            <div className="mb-6 flex justify-end">
                <button
                    className={clsx(
                        "secondary flex min-w-0 items-center gap-2",
                    )}
                    onClick={handleGenerateMetrics}
                    disabled={isPolling || !company.data?.uuid}
                >
                    {isPolling ? (
                        <>
                            <Spinner className="shrink-0" />
                            <span className="truncate">
                                {getFormattedMessage()}
                            </span>
                        </>
                    ) : (
                        <>
                            <ArrowPathIcon className="size-4" strokeWidth={2} />{" "}
                            Refresh Metrics
                        </>
                    )}
                </button>
            </div>

            <div className="space-y-12">
                <DetailTable
                    title="General"
                    data={generalData}
                    isLoading={isLoadingMetrics}
                />
                <DetailTable
                    title="Balance Sheet"
                    data={balanceSheetData}
                    isLoading={isLoadingMetrics}
                />
                <DetailTable
                    title="Income Statement"
                    data={incomeStatementData}
                    isLoading={isLoadingMetrics}
                />
                <DetailTable
                    title="Capital Structure"
                    data={capitalStructureData}
                    isLoading={isLoadingMetrics}
                />
                <div>
                    <h4 className="heading-m mb-4">
                        Projected Financials Table (next 3 years)
                    </h4>
                    <MemoTable
                        data={projectedFinancialsData}
                        columns={projectedFinancialsColumns}
                    />
                </div>
                <DetailTable
                    title="Projected Post-Closing Metrics"
                    data={metricsProjectionsData}
                    isLoading={isLoadingMetrics}
                />
            </div>
            <JumpToSection
                targetSection="financial-highlights"
                label="Jump to Financials & Projections"
            />
        </>
    );
}
